import { createContext, useCallback, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const LegalContext = createContext(null);

export const useLegal = () => useContext(LegalContext);

const SUPPORT_EMAIL = 'support@qreativs.com';

const linkifySupportEmail = (text) => {
  if (!text || typeof text !== 'string') return text;
  if (!text.includes(SUPPORT_EMAIL)) return text;
  const parts = text.split(SUPPORT_EMAIL);
  return parts.reduce((acc, part, i) => {
    acc.push(part);
    if (i < parts.length - 1) {
      acc.push(
        <a
          key={`mail-${i}`}
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-[#8b83ff] hover:text-[#6C63F5] font-medium underline underline-offset-2 transition-colors"
        >
          {SUPPORT_EMAIL}
        </a>
      );
    }
    return acc;
  }, []);
};

const PAGES = {
  about: {
    title: 'About us',
    body: [
      { h: 'A new-age creative community',
        p: 'Qreativs is a platform for all creative professionals and new age businesses to step into the new world of creative work and networking. Connect, collaborate, and grow with creatives, mentors and businesses. Get advice and insights from others in the creative industry. Find creatives to collaborate with and be discovered by businesses to work on projects.' },
      { h: 'Where brands and creatives build together',
        p: 'Businesses can find the right creatives to bring their vision to life. A new age community where creatives and brands build together openly and effortlessly. Join Qreativs to connect, create and collaborate with creatives around you.' },
    ],
  },
  contact: {
    title: 'Contact us',
    body: [
      { h: 'Send us an email',
        p: SUPPORT_EMAIL },
    ],
  },
  terms: {
    title: 'Terms & Conditions',
    body: [
      { h: 'Welcome to Qreativs!',
        p: 'By using our platform, you agree to these Terms & Conditions. Please read them carefully.' },
      { h: 'Acceptance of Terms',
        p: 'By creating an account or using Qreativs, you agree to comply with these Terms & Conditions and our Community Guidelines. If you do not agree, please do not use our services.' },
      { h: 'Eligibility',
        p: 'You must be at least 13 years old (or the minimum age required in your country) to use Qreativs. By using our platform, you represent that you meet this requirement.' },
      { h: 'Your Content',
        p: 'You retain ownership of the content you create and share on Qreativs. However, by posting content, you grant Qreativs a non-exclusive, worldwide, royalty-free license to display and promote your work within the platform and in connection with Qreativs\' services. You are responsible for ensuring that your content does not infringe on others\' intellectual property rights.' },
      { h: 'Prohibited Conduct',
        p: 'Violation of our community guidelines. Post content that is illegal, harmful, or offensive. Harass, spam, or impersonate others. Misuse reporting or moderation tools. Attempt to access unauthorized areas of Qreativs.' },
      { h: 'Account Suspension & Termination',
        p: 'We reserve the right to suspend or terminate your account if you violate these Terms, our Community Guidelines, or applicable laws. Where possible, we\'ll notify you and provide an opportunity to appeal.' },
      { h: 'Intellectual Property',
        p: 'All Qreativs branding, design, and platform features are the property of Qreativs and protected under copyright and trademark laws. You may not use our trademarks without permission.' },
      { h: 'Disclaimer of Warranties',
        p: 'Qreativs is provided "as is" and we make no warranties regarding the platform\'s performance or availability. Use it at your own risk.' },
      { h: 'Limitation of Liability',
        p: 'To the fullest extent permitted by law, Qreativs is not liable for any indirect or consequential damages arising from your use of the platform.' },
      { h: 'Changes to Terms',
        p: 'We may update these Terms occasionally. We\'ll notify you of significant changes. Continued use of Qreativs means you accept the revised terms.' },
      { h: 'Contact Us',
        p: 'If you have questions about these Terms, reach out to us at support@qreativs.com' },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    body: [
      { h: 'Our commitment',
        p: 'At Qreativs, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and your rights.' },
      { h: 'Information You Provide',
        p: 'Name, username, email address, and profile information. Creative work you upload or share. Messages you send through the platform. Payment information (processed securely via third-party providers).' },
      { h: 'Automatically Collected Information',
        p: 'Usage data (e.g., pages viewed, projects browsed). Device information (browser type, IP address, operating system).' },
      { h: 'How We Use Your Information',
        p: 'Provide and improve the Qreativs platform. Match creators with projects. Communicate with you about your account, activities, and updates. Personalize your experience. Ensure the safety and security of our community. Comply with legal obligations.' },
      { h: 'Sharing Your Information',
        p: 'We do not sell your personal information. We may share your data with: other users, when you publish public content; service providers (e.g., payment processors, cloud hosting); law enforcement or legal bodies when required by law.' },
      { h: 'How We Protect Your Information',
        p: 'We use industry-standard security practices (encryption, secure servers, access controls) to protect your data. However, no system is 100% secure.' },
      { h: 'Your Rights',
        p: 'Depending on your location, you may have the right to: access the personal data we hold about you; correct or delete your personal data; object to or restrict certain uses of your data; export your data in a portable format.' },
      { h: 'Data Retention',
        p: 'We retain your information while your account is active and as needed to comply with legal, tax, or operational requirements.' },
      { h: 'Changes to This Policy',
        p: 'We may update this Privacy Policy from time to time. We\'ll notify you of significant changes. Your continued use of Qreativs means you accept the updated policy.' },
      { h: 'Contact Us',
        p: 'If you have any questions or concerns about this Privacy Policy, please reach out to us at support@qreativs.com.' },
    ],
  },
  community: {
    title: 'Community guidelines',
    body: [
      {
        p: 'At Qreativs, we’re building a vibrant, inspiring space for creators from all backgrounds. To maintain a safe, respectful, and creative environment, we ask all members of our community to follow these guidelines.',
      },
      { h: 'What We Encourage' },
      {
        h: 'Respect & Kindness',
        p: 'Treat everyone with respect. Constructive feedback is welcome, but personal attacks, insults, or harassment will not be tolerated.',
      },
      {
        h: 'Originality & Creativity',
        p: 'Share your original work or properly credit collaborations. Inspire others with your unique ideas.',
      },
      {
        h: 'Positive Collaboration',
        p: 'Work together respectfully. Honor project agreements and be communicative and professional in your collaborations.',
      },
      {
        h: 'Inclusive Participation',
        p: 'We welcome creators of all backgrounds, identities, and experiences. Discrimination of any kind is strictly prohibited.',
      },
      {
        h: 'Safe Sharing',
        p: 'Share content appropriate for a general creative community. If your work includes sensitive or mature themes, please use the appropriate content warnings.',
      },
      { h: 'What’s Not Allowed' },
      {
        h: 'Hate Speech or Harassment',
        p: 'Any content that attacks individuals or groups based on race, ethnicity, religion, gender, orientation, disability, or other protected characteristics.',
      },
      {
        h: 'Spam or Self-Promotion Abuse',
        p: 'Spammy posts, repetitive self-promotion without engagement, and irrelevant links detract from the community experience.',
      },
      {
        h: 'Plagiarism & Intellectual Property Violations',
        p: 'Do not share work that you don’t own or have permission to share.',
      },
      {
        h: 'Inappropriate or Harmful Content',
        p: 'No sexually explicit, violent, threatening, or otherwise harmful content. This includes threats of harm to yourself or others.',
      },
      {
        h: 'Dishonest Behavior',
        p: 'Misrepresentation of your identity, credentials, or work, as well as scamming or fraud, is strictly prohibited.',
      },
      {
        h: 'Reporting Issues',
        p: 'If you encounter content or behavior that violates these guidelines, please report it using our reporting tools. Our moderation team reviews all reports carefully and takes action as needed.',
      },
      {
        h: 'Enforcement',
        p: 'Violations of these guidelines may result in:',
        list: [
          'A warning',
          'Removal of content',
          'Temporary account suspension',
          'Permanent account removal',
        ],
      },
      {
        p: 'We strive to apply these actions fairly and transparently.',
      },
      {
        h: 'Help Us Grow a Safe Creative Space',
        p: 'These guidelines are a shared responsibility. Together, we can make Qreativs a supportive and inspiring space where creativity thrives. If you have any questions or feedback, reach out to us at support@qreativs.com.',
      },
    ],
  },
};

export const LegalProvider = ({ children }) => {
  const [open, setOpen] = useState(null);
  const openPage = useCallback((key) => setOpen(key), []);
  const close = useCallback(() => setOpen(null), []);
  const page = open ? PAGES[open] : null;

  return (
    <LegalContext.Provider value={{ openPage, close }}>
      {children}
      <AnimatePresence>
        {page && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            data-testid={`legal-modal-${open}`}
          >
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={close} />
            <motion.article
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 max-h-[88vh] flex flex-col"
            >
              <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-zinc-800">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#8b83ff] font-semibold mb-2">Qreativs</div>
                  <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tighter text-zinc-50">{page.title}</h2>
                </div>
                <button
                  type="button"
                  onClick={close}
                  data-testid="legal-modal-close"
                  aria-label="Close"
                  className="text-zinc-500 hover:text-zinc-200 h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-zinc-900 transition-colors shrink-0"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-y-auto p-6 space-y-6">
                {page.body.map((s, i) => {
                  const isSectionOnly = s.h && !s.p && !s.list;
                  return (
                    <div key={i}>
                      {s.h && (
                        <h3
                          className={`font-display font-semibold text-zinc-100 mb-2 ${
                            isSectionOnly
                              ? 'text-xl tracking-tight text-[#c9c5ff]'
                              : 'text-lg'
                          }`}
                        >
                          {s.h}
                        </h3>
                      )}
                      {s.p && (
                        <p className="text-sm md:text-[0.95rem] text-zinc-400 leading-relaxed">
                          {linkifySupportEmail(s.p)}
                        </p>
                      )}
                      {Array.isArray(s.list) && s.list.length > 0 && (
                        <ul className="mt-3 space-y-1.5 list-disc pl-5 text-sm md:text-[0.95rem] text-zinc-400 leading-relaxed">
                          {s.list.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                <p className="pt-3 text-xs text-zinc-600 border-t border-zinc-800/70">
                  Last updated · {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </p>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </LegalContext.Provider>
  );
};

export default LegalProvider;
